import { skipToken, useQuery } from '@tanstack/react-query';

import useApiClients from '~hooks/useApiClients';
import { M2MClient } from '~hooks/useApiClients/models';
import fetchAllPages from '~utils/fetchAllPages';

import { RARELY_CHANGED_DATA_STALE_TIME_IN_MS } from './constants';

const COMPANY_API_CLIENTS_QUERY_KEY = 'companyApiClients';

const EMPTY_API_CLIENTS: M2MClient[] = [];

interface UseCompanyApiClientsQueryProps {
  companyId?: string;
  isEnabled: boolean;
}

export default function useCompanyApiClientsQuery({
  companyId,
  isEnabled,
}: UseCompanyApiClientsQueryProps) {
  const { fetchApiClients } = useApiClients();

  const { data, isError, isFetching, refetch } = useQuery({
    queryKey: [COMPANY_API_CLIENTS_QUERY_KEY, companyId],
    queryFn:
      isEnabled && companyId
        ? ({ signal }) =>
            fetchAllPages(
              (cursor, limit) =>
                fetchApiClients({
                  companyId,
                  limit,
                  ...(cursor ? { link: 'after' as const, cursor } : {}),
                }),
              { signal },
            )
        : skipToken,
    // fetchApiClients already toasts and reports through connection.handleRequestError,
    // so React Query's default of three retries would surface one failure four times.
    retry: false,
    // API clients are managed on their own settings page, so a caller that mounts and
    // unmounts repeatedly should not re-walk every page each time.
    staleTime: RARELY_CHANGED_DATA_STALE_TIME_IN_MS,
  });

  return { apiClients: data ?? EMPTY_API_CLIENTS, isError, isFetching, refetch };
}
